import { memo, useRef } from "react";
import styled, { useTheme } from "styled-components";
import type { createTimelineSegmentIndex } from "./timelineSegmentIndex";
import { paintTimelineSegmentCanvas } from "./timelineSegmentCanvasPaint";
import { segmentLineOf } from "./timelineSegmentPaint";
import {
  measureTimelineCanvas,
  prepareTimelineCanvas,
  useTimelineRowCanvas,
  type TimelineCanvasGeometry,
} from "./timelineRowCanvas";
import { resolveCanvasToken } from "./timelineTickCanvas";

const BandCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

interface Props {
  index: ReturnType<typeof createTimelineSegmentIndex>;
  viewportStart: number;
  viewportDuration: number;
}

/** One backing bitmap per row; playback and range edits do not invalidate static bands. */
export const TimelineSegmentsCanvas = memo(function TimelineSegmentsCanvas({
  index,
  viewportStart,
  viewportDuration,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  const lastPaintRef = useRef<
    | {
        geometry: TimelineCanvasGeometry;
        palette: ReturnType<typeof paintTimelineSegmentCanvas>;
      }
    | undefined
  >(undefined);
  useTimelineRowCanvas(
    canvasRef,
    (canvas, invalidated) => {
      const geometry = measureTimelineCanvas(canvas);
      if (!geometry) return false;
      const last = lastPaintRef.current;
      if (
        !invalidated &&
        last &&
        last.geometry.width === geometry.width &&
        last.geometry.height === geometry.height &&
        last.geometry.bitmapWidth === geometry.bitmapWidth &&
        last.geometry.bitmapHeight === geometry.bitmapHeight &&
        Array.from(last.palette).every(
          ([tone, colors]) =>
            colors.line === resolveCanvasToken(segmentLineOf(theme, tone), canvas),
        )
      )
        return true;
      const context = prepareTimelineCanvas(canvas, geometry);
      if (!context) return false;
      const palette = paintTimelineSegmentCanvas(
        context,
        index.query(viewportStart, viewportStart + viewportDuration),
        { width: geometry.width, height: geometry.height, viewportStart, viewportDuration, theme },
      );
      lastPaintRef.current = { geometry, palette };
      return true;
    },
    [index, viewportStart, viewportDuration, theme],
  );
  return <BandCanvas ref={canvasRef} aria-hidden="true" data-media-segments-canvas="" />;
});
